import React, { useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { groupBy } from 'lodash';
import i18n from 'i18next';

import { useMediaQuery } from 'modules/Core/Hooks';
import { Selectors as AuthSelectors } from 'modules/Auth/AuthSlice';
import { prettyUserType } from 'modules/Users/Permissions/UserType';
import {
  LogoutIcon,
  SettingsIcon,
  InstallMobileIcon,
  DashboardSecondIcon,
} from '@edusynch/edusynch-svg-icons';
import defaultLogo from 'assets/common/logo-edusynch.png';
import { SidebarHeader } from './SidebarHeader';
import { SidebarLink } from './SidebarLink';
import { SidebarUnclickableLink } from './SidebarUnclickableLink';
import SidebarSubmenu from './SidebarSubmenu.component';
import { useLayout } from '../Hooks/useLayout';
import { Tooltip } from 'react-tooltip';
import * as S from './Sidebar.style';

const formatStringToTranslation = (name) =>
  name?.replaceAll(' ', '-').toLowerCase();

const SECTION_ORDER = ['menu', 'super_admin'];
const SECTION_LABELS = {
  menu: 'MENU',
  super_admin: 'SUPER ADMIN',
};

const DROPDOWN_PREFIX = 'header.dropdown-options';

const Sidebar = (props) => {
  const {
    logo,
    routes,
    opened,
    clicked,
    activeMenuName,
    openSidebarAbsolute,
    setOpenDashboardShortcut,
    closeSidebarMouseLeave,
    openedResponsive,
    toggleResponsiveSidebarOpen,
    toggleResponsiveSidebarClose,
    toggleCloseSidebar,
    toggleSidebarDeactivated,
    sidebarExpanded,
    toggleSidebarExpanded,
    whitelabelName,
    onLogoutClick,
    outcome,
  } = props;

  const asideRef = useRef(null);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const session = useSelector(AuthSelectors.selectAuthData);
  const user = session?.user;
  const [expandedMenuItem, setExpandedMenuItem] = useState(null);
  const { topMargin } = useLayout();

  const filteredRoutes = routes.filter(
    (route) => !route.hidden && route.show_on_menu
  );
  const routesBySection = groupBy(filteredRoutes, 'section');

  useEffect(() => {
    if (isDesktop) toggleResponsiveSidebarOpen();
    else {
      toggleResponsiveSidebarClose();
      toggleCloseSidebar();
      toggleSidebarDeactivated();
      setOpenDashboardShortcut(false);
    }
  }, [isDesktop]);

  const closeSidebarMenu = useCallback(() => {
    if (opened) setOpenDashboardShortcut(true);
    if (!isDesktop) toggleResponsiveSidebarClose();
    closeSidebarMouseLeave();
  }, [opened, isDesktop]);

  const handleSettings = useCallback(() => {
    navigate('/settings-profile');
    if (!isDesktop) toggleResponsiveSidebarClose();
  }, [navigate, isDesktop]);

  const handleWidgets = useCallback(() => {
    navigate('/dashboard/widgets');
    if (!isDesktop) toggleResponsiveSidebarClose();
  }, [navigate, isDesktop]);

  const handleLogout = useCallback(() => {
    onLogoutClick?.();
    if (!isDesktop) toggleResponsiveSidebarClose();
  }, [onLogoutClick, isDesktop]);

  const handleSubmenuToggle = useCallback((menuName) => {
    setExpandedMenuItem((prev) => (prev === menuName ? null : menuName));
  }, []);

  const renderMenuItems = (sectionRoutes) =>
    sectionRoutes.flatMap((items) => {
      const elements = [
        <S.SidebarListItem key={uuid()}>
          {!items.path ? (
            <SidebarUnclickableLink
              {...items}
              isDesktop={isDesktop}
              expanded={sidebarExpanded}
              isExpandedInline={expandedMenuItem === items.name}
              onClick={openSidebarAbsolute}
              onMobileSubmenuToggle={handleSubmenuToggle}
              activeMenuName={activeMenuName}
            />
          ) : (
            <SidebarLink
              {...items}
              onClick={closeSidebarMenu}
              isDesktop={isDesktop}
              expanded={sidebarExpanded}
            />
          )}
        </S.SidebarListItem>,
      ];

      if (
        items.submenu &&
        expandedMenuItem === items.name &&
        (sidebarExpanded || !isDesktop)
      ) {
        items.submenu.menus.forEach(
          ({ name: groupName, routes: groupRoutes }) => {
            const visibleRoutes = groupRoutes.filter(
              (r) => r.show_on_submenu && !r.hidden
            );
            if (visibleRoutes.length === 0) return;

            if (groupName) {
              elements.push(
                <S.SidebarListItem key={uuid()}>
                  <S.InlineSubmenuGroupTitle>
                    {i18n.t(
                      `sidebar.${formatStringToTranslation(
                        items.name
                      )}-submenu.${formatStringToTranslation(groupName)}`
                    )}
                  </S.InlineSubmenuGroupTitle>
                </S.SidebarListItem>
              );
            }

            visibleRoutes.forEach((route) => {
              elements.push(
                <S.SidebarListItem key={uuid()}>
                  <S.InlineSubmenuLink
                    to={route.path}
                    onClick={
                      !isDesktop ? toggleResponsiveSidebarClose : undefined
                    }
                    disabled={route.disabled}
                  >
                    {i18n.t(
                      `sidebar.${formatStringToTranslation(
                        items.name
                      )}-submenu.menu-items.${formatStringToTranslation(
                        route.name
                      )}`
                    )}
                  </S.InlineSubmenuLink>
                </S.SidebarListItem>
              );
            });
          }
        );
      }

      return elements;
    });

  return (
    <S.Container
      onMouseLeave={
        !sidebarExpanded && isDesktop && clicked ? closeSidebarMouseLeave : null
      }
    >
      <S.SidebarCSSTransition
        in={openedResponsive}
        timeout={200}
        unmountOnExit
        nodeRef={asideRef}
        classNames="sidebar-animation"
      >
        <>
          <S.AsideContainer>
            <S.Aside
              style={{ top: `${topMargin || 0}px` }}
              $expanded={sidebarExpanded}
            >
              <SidebarHeader
                logo={logo || defaultLogo}
                whitelabelName={whitelabelName}
                onToggleExpanded={toggleSidebarExpanded}
                onClose={toggleResponsiveSidebarClose}
              />

              <S.MenuContent ref={asideRef}>
                {!isDesktop && (
                  <S.MobileHeaderBlock>
                    <S.MobileUserInfo>
                      <S.MobileUserName>{user?.name}</S.MobileUserName>
                      <S.MobileUserEmail>{user?.email}</S.MobileUserEmail>
                      {user?.user_type && (
                        <S.MobileUserType>
                          {prettyUserType(user.user_type) || user.user_type}
                        </S.MobileUserType>
                      )}
                    </S.MobileUserInfo>
                    <S.MobileHeaderItem type="button" onClick={handleSettings}>
                      <SettingsIcon />
                      <span>{i18n.t(`${DROPDOWN_PREFIX}.settings`)}</span>
                    </S.MobileHeaderItem>
                    <S.MobileHeaderItem type="button" onClick={handleWidgets}>
                      <DashboardSecondIcon />
                      <span>{i18n.t(`${DROPDOWN_PREFIX}.widgets`)}</span>
                    </S.MobileHeaderItem>
                    {outcome && (
                      <S.MobileHeaderItem
                        type="button"
                        onClick={() => {
                          outcome?.prompt?.();
                          toggleResponsiveSidebarClose();
                        }}
                      >
                        <InstallMobileIcon />
                        <span>{i18n.t(`${DROPDOWN_PREFIX}.install`)}</span>
                      </S.MobileHeaderItem>
                    )}
                    <S.MobileHeaderItem type="button" onClick={handleLogout}>
                      <LogoutIcon />
                      <span>{i18n.t(`${DROPDOWN_PREFIX}.logout`)}</span>
                    </S.MobileHeaderItem>
                  </S.MobileHeaderBlock>
                )}
                {SECTION_ORDER.map((sectionKey, sectionIndex) => {
                  const sectionRoutes = routesBySection[sectionKey] || [];
                  if (sectionRoutes.length === 0) return null;

                  return (
                    <React.Fragment key={sectionKey}>
                      {sectionIndex > 0 && (
                        <S.MenuSectionSeparator $expanded={sidebarExpanded} />
                      )}
                      <S.MenuSection $expanded={sidebarExpanded}>
                        <S.MenuSectionTitle $expanded={sidebarExpanded}>
                          {sidebarExpanded
                            ? SECTION_LABELS[sectionKey] || sectionKey
                            : (SECTION_LABELS[sectionKey] || sectionKey).charAt(
                                0
                              )}
                        </S.MenuSectionTitle>
                        <S.SidebarList $expanded={sidebarExpanded}>
                          {renderMenuItems(sectionRoutes)}
                        </S.SidebarList>
                      </S.MenuSection>
                    </React.Fragment>
                  );
                })}
              </S.MenuContent>
            </S.Aside>
          </S.AsideContainer>
        </>
      </S.SidebarCSSTransition>

      {isDesktop && (
        <Tooltip
          id="sidebar-tooltip"
          place="right"
          hidden={sidebarExpanded}
          style={{
            backgroundColor: '#0075EA',
            color: '#fff',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            zIndex: 9999,
          }}
        />
      )}

      {!sidebarExpanded && (
        <SidebarSubmenu {...props} sidebarExpanded={sidebarExpanded} />
      )}
    </S.Container>
  );
};

Sidebar.defaultProps = {
  opened: false,
  sidebarExpanded: true,
};

Sidebar.propTypes = {
  routes: PropTypes.array,
  opened: PropTypes.bool,
  clicked: PropTypes.bool,
  logo: PropTypes.string,
  whitelabelName: PropTypes.string,
  openSidebarAbsolute: PropTypes.func,
  activeMenuName: PropTypes.string,
  closeSidebarMouseLeave: PropTypes.func,
  openedResponsive: PropTypes.bool,
  sidebarExpanded: PropTypes.bool,
  toggleSidebarExpanded: PropTypes.func,
  toggleResponsiveSidebarOpen: PropTypes.func,
  toggleResponsiveSidebarClose: PropTypes.func,
  setOpenDashboardShortcut: PropTypes.func,
  toggleCloseSidebar: PropTypes.func,
  toggleSidebarDeactivated: PropTypes.func,
  onLogoutClick: PropTypes.func,
  outcome: PropTypes.any,
};

export default React.memo(Sidebar);
